import { fill, formatNumber, messages, type Lang, type Messages } from "../i18n/index";
import type { RenderInfo } from "../render/index";
import { blurOnPointerClick, button, localize, setText } from "./dom";
import type { Store } from "./store";

const WINDOW_MS = 500;

export interface FrameWindow {
  fps: number;
  /** The longest interval between two frames: a hitch the average hides. */
  slowestMs: number;
  /** Mean JS time per frame; far below the interval means the GPU or browser is the limit. */
  cpuMs: number;
}

/** Sums frame timings over half-second windows, so the readout changes slowly enough to read. */
export class FrameMeter {
  private start = -1;
  private last = 0;
  private frames = 0;
  private slowest = 0;
  private cpu = 0;

  /** Adds a frame drawn at `now` whose work took `cpuMs`; returns the window it closes, else null. */
  sample(now: number, cpuMs: number): FrameWindow | null {
    // the first frame after a reset has no interval
    if (this.start < 0) {
      this.start = this.last = now;
      return null;
    }
    this.frames++;
    this.slowest = Math.max(this.slowest, now - this.last);
    this.cpu += cpuMs;
    this.last = now;
    const elapsed = now - this.start;
    if (elapsed < WINDOW_MS) return null;
    const window = {
      fps: (1000 * this.frames) / elapsed,
      slowestMs: this.slowest,
      cpuMs: this.cpu / this.frames,
    };
    this.start = now;
    this.frames = this.slowest = this.cpu = 0;
    return window;
  }

  /** Forgets the open window, e.g. while the tab was hidden and drew nothing. */
  reset(): void {
    this.start = -1;
    this.frames = this.slowest = this.cpu = 0;
  }
}

export type StatsInput = FrameWindow &
  RenderInfo & { drawCalls: number; keplerNonConverged: number; nonFinite: number };

export function formatStats(s: StatsInput, lang: Lang): string {
  return fill(messages(lang).stats, {
    fps: formatNumber(lang, s.fps),
    slowest: formatNumber(lang, s.slowestMs, 1),
    cpu: formatNumber(lang, s.cpuMs, 1),
    // device pixels read better without a thousands separator
    width: s.width,
    height: s.height,
    ratio: formatNumber(lang, s.pixelRatio, 2, 0),
    draws: s.drawCalls,
    textures: s.textures,
    geometries: s.geometries,
    misses: s.keplerNonConverged,
    nonFinite: s.nonFinite,
  });
}

/** How long "Copied" or "Copy failed" stays on the button before it reads "Copy" again. */
const COPY_FEEDBACK_MS = 2000;

export function mountStats(
  container: HTMLElement,
  store: Store,
): {
  show(text: string): void;
  setVisible(v: boolean): void;
} {
  const el = document.createElement("div");
  el.className = "stats";
  el.style.display = "none";
  // its own node, so the twice-a-second refresh leaves the button alone
  const text = document.createElement("span");
  text.className = "stats-text";
  // pasted into an issue or a chat, the lines say more than a screenshot of them
  const copy = button(
    "",
    () =>
      // inside a promise, so a page without the Clipboard API (plain http) reports the failure too
      void Promise.resolve()
        .then(() => navigator.clipboard.writeText(text.textContent))
        .then(
          () => report("copied"),
          () => report("failed"),
        ),
    "stats-copy",
  );
  blurOnPointerClick(copy);
  let state: keyof Messages["statsCopy"] = "copy";
  let timer = 0;
  const label = (): void => setText(copy, messages(store.state.lang).statsCopy[state]);
  const report = (next: typeof state): void => {
    state = next;
    label();
    clearTimeout(timer);
    if (next !== "copy") timer = window.setTimeout(() => report("copy"), COPY_FEEDBACK_MS);
  };
  el.append(text, copy);
  container.append(el);
  localize(store, label);
  return {
    show: (next) => setText(text, next),
    setVisible: (v) => {
      el.style.display = v ? "block" : "none";
    },
  };
}
